import {
  createPanelFrame,
  resolveTitle,
  createMetric,
  formatNumber,
  formatPercent,
  toNumber,
  clamp
} from './panel-utils.js';

const DEFAULT_STAGES = [
  { id: 'ingest', label: 'Ingest', throughput: 520, capacity: 800, latency: 120, errorRate: 0.01 },
  { id: 'queue', label: 'Queue', throughput: 430, capacity: 700, latency: 210, errorRate: 0.02 },
  { id: 'transform', label: 'Transform', throughput: 390, capacity: 650, latency: 280, errorRate: 0.015 },
  { id: 'index', label: 'Index', throughput: 340, capacity: 600, latency: 310, errorRate: 0.03 }
];

export function render({ node }) {
  const { root, body, titleEl, metaEl } = createPanelFrame(node, 'Throughput Stream');

  const metrics = document.createElement('div');
  metrics.className = 'ew-metric-grid';

  const canvasWrap = document.createElement('div');
  canvasWrap.className = 'ew-stream-canvas';
  const canvas = document.createElement('canvas');
  canvas.width = 360;
  canvas.height = 180;
  canvasWrap.appendChild(canvas);

  const footer = document.createElement('div');
  footer.className = 'ew-particle-footer';

  body.append(metrics, canvasWrap, footer);

  const ctx = canvas.getContext('2d');
  const state = {
    particles: [],
    lastTs: 0,
    raf: null,
    resizeObserver: null,
    data: null
  };

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Throughput Stream');
    const data = normalizeData(next);
    state.data = data;

    metaEl.textContent = data.window;

    metrics.innerHTML = '';
    metrics.append(
      createMetric('req/s', formatNumber(data.totalThroughput)),
      createMetric('capacity', formatNumber(data.totalCapacity)),
      createMetric('latency', `${formatNumber(data.avgLatency)}ms`),
      createMetric('errors', formatPercent(data.errorRate, 1))
    );

    footer.textContent = data.mode === 'burst' ? 'Burst routing' : 'Stable flow';

    ensureParticles(state);
    startLoop();
  };

  const startLoop = () => {
    if (state.raf) return;
    const tick = (ts) => {
      state.raf = requestAnimationFrame(tick);
      const delta = state.lastTs ? ts - state.lastTs : 16;
      state.lastTs = ts;
      drawFrame(state, ctx, delta);
    };
    state.raf = requestAnimationFrame(tick);
  };

  const stopLoop = () => {
    if (state.raf) cancelAnimationFrame(state.raf);
    state.raf = null;
  };

  state.resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const { width, height } = entry.contentRect;
      canvas.width = Math.max(240, Math.floor(width));
      canvas.height = Math.max(140, Math.floor(height));
    }
  });
  state.resizeObserver.observe(canvasWrap);

  update(node);

  return {
    el: root,
    update,
    cleanup: () => {
      stopLoop();
      state.resizeObserver?.disconnect();
    }
  };
}

function normalizeData(node) {
  const raw = node?.data || node?.props || {};
  const stages = Array.isArray(raw.stages) && raw.stages.length
    ? raw.stages.map((s, i) => ({
      id: String(s.id ?? `stage-${i}`),
      label: s.label || s.id || `Stage ${i + 1}`,
      throughput: toNumber(s.throughput, 300 + i * 40),
      capacity: toNumber(s.capacity, 500 + i * 60),
      latency: toNumber(s.latency, 200 + i * 40),
      errorRate: clamp(toNumber(s.errorRate, 0.01), 0, 1),
      color: s.color || null
    }))
    : DEFAULT_STAGES;

  const totalThroughput = stages.reduce((acc, s) => acc + s.throughput, 0);
  const totalCapacity = stages.reduce((acc, s) => acc + s.capacity, 0);
  const avgLatency = stages.length ? stages.reduce((acc, s) => acc + s.latency, 0) / stages.length : 0;
  const errorRate = stages.length ? stages.reduce((acc, s) => acc + s.errorRate, 0) / stages.length : 0;

  return {
    stages,
    window: raw.window || '5m',
    mode: raw.mode || 'steady',
    totalThroughput,
    totalCapacity,
    avgLatency: Math.round(avgLatency),
    errorRate,
    particleCount: clamp(toNumber(raw.particleCount, 500), 200, 1200),
    turbulence: clamp(toNumber(raw.turbulence, 0.35), 0, 1)
  };
}

function ensureParticles(state) {
  const target = state.data?.particleCount || 500;
  while (state.particles.length < target) state.particles.push(spawnParticle(state));
  if (state.particles.length > target) state.particles.length = target;
}

function spawnParticle(state) {
  const laneIndex = Math.floor(Math.random() * state.data.stages.length);
  return {
    laneIndex,
    x: Math.random(),
    speed: 0.15 + Math.random() * 0.6,
    jitter: Math.random()
  };
}

function drawFrame(state, ctx, delta) {
  if (!state.data) return;
  const { stages, turbulence, mode } = state.data;
  const width = ctx.canvas.width;
  const height = ctx.canvas.height;
  ctx.clearRect(0, 0, width, height);

  ctx.fillStyle = 'rgba(5, 18, 30, 0.85)';
  ctx.fillRect(0, 0, width, height);

  const laneCount = stages.length || 1;
  const laneHeight = height / laneCount;

  stages.forEach((stage, idx) => {
    const y = idx * laneHeight;
    const utilization = stage.capacity ? clamp(stage.throughput / stage.capacity, 0, 1) : 0;
    const laneMid = y + laneHeight / 2;

    ctx.strokeStyle = 'rgba(95, 234, 255, 0.18)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, laneMid);
    ctx.lineTo(width, laneMid);
    ctx.stroke();

    ctx.fillStyle = 'rgba(95, 234, 255, 0.12)';
    ctx.fillRect(0, y + laneHeight * 0.25, width * utilization, laneHeight * 0.5);

    ctx.fillStyle = 'rgba(234, 255, 255, 0.7)';
    ctx.font = '10px "Share Tech Mono", monospace';
    ctx.textAlign = 'left';
    ctx.fillText(stage.label, 8, y + 12);
  });

  state.particles.forEach((p) => {
    const lane = stages[p.laneIndex % stages.length];
    const speedScale = clamp(lane.throughput / Math.max(lane.capacity, 1), 0.2, 1.2);
    const speedBoost = mode === 'burst' ? 1.4 : 1;
    p.x += (p.speed * speedScale * speedBoost * delta) / 2000;
    if (p.x > 1) {
      p.x = 0;
      p.laneIndex = Math.floor(Math.random() * stages.length);
    }

    const laneIndex = p.laneIndex % stages.length;
    const yCenter = laneIndex * laneHeight + laneHeight / 2;
    const y = yCenter + (Math.random() - 0.5) * turbulence * laneHeight * 0.25;
    const x = p.x * width;

    const alpha = 0.3 + speedScale * 0.5;
    ctx.fillStyle = lane.color
      ? lane.color
      : `rgba(${Math.floor(80 + speedScale * 120)}, ${Math.floor(200 - lane.errorRate * 120)}, 255, ${alpha})`;
    ctx.beginPath();
    ctx.arc(x, y, 1.2 + speedScale * 1.4, 0, Math.PI * 2);
    ctx.fill();
  });
}
